import type { Translations } from "./index";

const en: Translations = {
  header: { title: "Drumlog", subtitle: "Your Practice Companion" },
  metronome: { title: "Metronome", tapTempo: "Tap Tempo", timeSignature: "Time Signature" },
  practiceSession: {
    title: "Practice Session", start: "Start Session", stop: "Stop", pause: "Pause", resume: "Resume",
    thisWeek: "This Week", streak: "Streak", days: "Days", recentSessions: "Recent Sessions",
    today: "Today", weeklyProgress: "Weekly Progress", latestSession: "Latest Session", startFirstSession: "Start first session",
    freePractice: "Free Practice", noRecentSessions: "No recent sessions",
    studioToday: "Today in the Studio", readyToPractice: "Ready for the next session", sessionRunning: "Session running", sessionPaused: "Session paused",
    bestDay: "Best Day", avgActiveDay: "Avg active day", weekEmptyHint: "Start a session to make your weekly rhythm visible.",
    onboardingTitle: "Welcome to the studio", onboardingBody: "Start your first session – your stats will show up here automatically.",
    onboardingMidiHint: "Tip: connect your MIDI kit for hit tracking.",
    editSession: "Edit Session", sessionTitle: "Title", sessionNotes: "Notes",
    sessionNotesPlaceholder: "What did you practice? Progress, songs, exercises...",
    sessionSaved: "Session saved", deleteSession: "Delete Session", sessionDeleted: "Session deleted",
    duration: "Duration", back: "Back",
    addExercise: "Add Exercise", exerciseTitle: "Name", exerciseTitlePlaceholder: "e.g. Single Stroke Roll, Back in Black...",
    exerciseType: "Type", typeWarmup: "Warm-Up", typeRudiment: "Rudiment", typeSong: "Song",
    sessionItems: "Practiced Items", noSessionItems: "No items yet – add what you practiced",
    addToSession: "Add",
    fromLibrary: "From Library", createNew: "Create New", searchPlaceholder: "Search...", newItemCreated: "New item created",
    convertHint: "Turn this free session into a structured practice – add exercises, songs, or warm-ups below!",
  },
  premium: { title: "Full Features", description: "Record your practice sessions, listen back, and track your progress over time.", unlock: "Unlock Premium", badge: "Premium", activeBadge: "Premium Active", activeDescription: "You have full access to all features." },
  stats: {
    title: "Statistics", last7Days: "Last 7 Days (Minutes)", last4Weeks: "Last 4 Weeks (Minutes)",
    total: "Total", sessions: "Sessions", avgPerSession: "Avg per Session", streak: "Streak",
    days: "Days", minutes: "Min", hours: "h", noSessions: "No sessions recorded yet. Start your first practice session!",
    currentWeek: "current", weekPrefix: "W",
    milestones: "Milestones", nextMilestone: "Next Milestone", dailyGoal: "Daily Goal", goalReached: "Goal reached!",
    midiPremiumTitle: "MIDI Performance",
    midiPremiumDesc: "Unlock detailed hit tracking, instrument distribution, and performance trends with a MIDI controller.",
    midiPremiumCta: "Unlock Premium",
    midiConnectTitle: "MIDI Performance",
    midiConnectDesc: "Connect a MIDI controller and record a practice session to start tracking your performance stats here.",
    midiConnectCta: "Start Session",
  },
  nav: { stats: "Statistics", drumPattern: "Drum Pattern", sessionMode: "Session Mode", faq: "FAQ" },
  notFound: { title: "404", message: "Page not found", backHome: "Return to Home" },
  drumPattern: {
    title: "Drum Pattern", subtitle: "Create & play drum beats", play: "Play", stop: "Stop",
    bpm: "BPM", metronome: "Metronome", clear: "Clear", clearConfirm: "Clear all beats?",
    export: "Export", import: "Import",
    kick: "Kick", snare: "Snare", hihat: "Hi-Hat", openhihat: "Open Hi-Hat", pedalhat: "Hi-Hat (Foot)",
    hitom: "Hi Tom", midtom: "Mid Tom", lotom: "Lo Tom", crash: "Crash", ride: "Ride", splash: "Splash",
    steps16: "16 Steps", steps32: "32 Steps", patternSaved: "Pattern exported", patternLoaded: "Pattern imported", patternTruncated: "Pattern truncated to allowed measures",
    addTrack: "Add Track", removeTrack: "Remove", noTracksMessage: "Add a track to get started",
    measures: "Measures", patterns: "Patterns", myPatterns: "My Patterns", presets: "Presets",
    savePattern: "Save", namePlaceholder: "Pattern name...", noPatterns: "No saved patterns yet",
    deletePattern: "Delete", patternDeleted: "Pattern deleted",
    presetRock: "Basic Rock", presetFunk: "Funk", presetShuffle: "Shuffle",
    presetBossaNova: "Bossa Nova", presetReggae: "Reggae", presetDnB: "Drum & Bass",
    maxPatternsReached: "Limit reached (3) – ", premiumPresetsLocked: "Premium required", upgradeToPremium: "Unlock Premium", premiumPresets: "Premium Presets",
    copyMeasure: "Copy Measure", measureLabel: "Measure", measureCopied: "Measure copied", moveMeasure: "Drag to reorder",
    midi: {
      connect: "Connect MIDI", disconnect: "Disconnect", device: "MIDI Device", mapping: "MIDI Mapping",
      learn: "Learn", learning: "Hit pad...", record: "Record", recording: "Recording", stopRecording: "Stop",
      score: "Hit Rate", tolerance: "Tolerance", noDevice: "No MIDI device found",
      notSupported: "MIDI is not supported in this browser. Please use Chrome or Edge.",
      premiumRequired: "Premium required", patternRecorded: "Pattern recorded",
      latency: "Latency", latencyHint: "Compensate for hardware delay. Increase if hits register too late.",
      tapTest: "Tap-Test", tapTestStop: "Stop", tapTestApply: "Apply",
      tapTestHint: "Hit your pad steadily 8-10 times to measure hardware latency.",
      midiSound: "MIDI Sound", midiVolume: "MIDI Volume",
      wizard: "Wizard",
      wizardTitle: "MIDI Setup Wizard", wizardStep: "Step",
      wizardHitPrompt: "Now hit your:", wizardMultiZone: "Hit all zones of this pad/cymbal. Press Next when done.",
      wizardWaiting: "Waiting for MIDI input...", wizardRestart: "Restart",
      wizardSkip: "Skip", wizardNext: "Next", wizardFinish: "Finish",
    },
    mute: "Mute", unmute: "Unmute", patternVolume: "Pattern", countIn: "Count-In",
    metronomeSound: "Sound", classicSynth: "Classic/Synth", premiumLockedShort: "Premium",
    timelineView: "Timeline", notation: "Sheet",
    moreMeasures: "More Measures", moreMeasuresHint: "Unlock up to 64 measures with Full Features",
    measureLimitReached: "Measure limit reached",
  },
  auth: {
    loginTitle: "Login", registerTitle: "Register", forgotPasswordTitle: "Reset Password", newPasswordTitle: "Set New Password",
    email: "Email", password: "Password", firstName: "First name", lastName: "Last name",
    loginButton: "Log in", registerButton: "Register", sendResetLink: "Send Reset Link", updatePassword: "Update Password",
    forgotPassword: "Forgot password?", noAccount: "No account? Register", hasAccount: "Already have an account? Login", rememberMe: "Remember me",
    backToLogin: "Back to login", checkEmail: "Check your email", confirmEmail: "We sent you a confirmation link.",
    loginError: "Login failed", registerError: "Registration failed", passwordTooShort: "Password must be at least 6 characters",
    resetEmailSent: "Check your email for the reset link.", passwordUpdated: "Password updated successfully",
    invalidResetLink: "Invalid or expired reset link.", newPassword: "New password",
    profile: "Profile", admin: "Admin", logout: "Logout", save: "Save", profileSaved: "Profile saved", plan: "Plan", loginRequired: "Login required",
    verifying: "Verifying...", invalidLink: "Invalid or expired link.", linkExpired: "This link has expired or is invalid. Please request a new invite link.",
    dailyGoalLabel: "Daily Practice Goal (min)",
    username: "Username", emailOrUsername: "Email or Username",
    usernameTaken: "Username already taken", usernameInvalid: "3-30 chars, starts with letter, letters/numbers/_ only",
    notifications: "Notifications", emailNotifications: "Email notifications",
    notifyStatusChange: "Status changes", notifyNewComment: "New comments",
    alreadyLoggedIn: "You are logged in as", goToDashboard: "Go to Dashboard", switchUser: "Sign in as different user", adminPanel: "Admin Panel",
    registrationClosed: "Registration Closed", registrationClosedMessage: "Registration is currently closed. Please check back later.", registrationClosedWaitlist: "Join the waitlist",
    earlyAccessBadge: "Early Access",
    earlyAccessTitle: "Secure your early access spot",
    earlyAccessDescription: "drumLog.Pro is currently in a closed beta. Join the waitlist and be one of the first to help shape the future.",
    earlyAccessBenefit1: "Free access during the beta phase",
    earlyAccessBenefit2: "Direct influence on new features and roadmap",
    earlyAccessBenefit3: "Discounted pricing on premium features at official launch",
    earlyAccessCta: "Join the waitlist now",
    earlyAccessLoginHint: "Already registered?",
    privacyConsent: "By registering you agree to our",
    privacyPolicy: "Privacy Policy",
    imprint: "Imprint",
    // Account deletion
    deleteAccount: "Delete account",
    deleteAccountDesc: "Request permanent deletion of your account. An administrator will review your request and then irrevocably remove all your data from our system (email, password, patterns, practice data).",
    requestDeleteAccount: "Request account deletion",
    deleteAccountConfirmTitle: "Request account deletion",
    deleteAccountConfirmDesc: "This action will send a request to the administrators. After approval, all your data will be irrevocably deleted. Type the following word to confirm:",
    deleteAccountConfirmButton: "Submit request",
    deleteAccountReason: "Reason (optional, helps us)",
    deleteAccountReasonPlaceholder: "e.g. I no longer use the app...",
    deletionRequested: "You have requested account deletion on",
    deletionPendingHint: "An administrator is reviewing your request. Your account remains usable until then. You can withdraw the request at any time.",
    withdrawDeletionRequest: "Withdraw request",
    deletionRequestSuccess: "Account deletion request submitted.",
    deletionWithdrawn: "Request withdrawn.",
    deletionRequestError: "Error saving the request.",
    typeWord: "Type the security word...",
    cancel: "Cancel",
    exportData: "Export your data",
    exportDataDesc: "Download a copy of all your data as JSON (profile, practice items, sessions, patterns). Complies with GDPR Art. 15 & 20.",
    exportDataButton: "Download data",
    exportDataLoading: "Preparing...",
    exportDataSuccess: "Export downloaded successfully.",
    exportDataError: "Export failed.",
    exportBeforeDeleteHint: "Would you like to back up your data first? After deletion it is gone forever.",
    exportNow: "Export now",
  },
  admin: {
    title: "Admin Dashboard", search: "Search...", addUser: "Add User", editUser: "Edit User", create: "Create",
    name: "Name", emailCol: "Email", role: "Role", planCol: "Plan", status: "Status", actions: "Actions",
    blocked: "Blocked", active: "Active", unconfirmed: "Unconfirmed", noUsers: "No users found",
    userDeleted: "User deleted", userUpdated: "User updated", userCreated: "User created",
    passwordLength: "Password must be 8-72 characters",
    maintenanceMode: "Maintenance Mode", maintenanceReason: "Maintenance Reason", maintenanceReasonPlaceholder: "e.g. We are performing a system update...",
    maintenanceActive: "Active", maintenanceInactive: "Inactive", maintenanceSaved: "Maintenance mode updated",
    settingsSaved: "Settings saved", appVersion: "App Version & Status", versionLabel: "Version",
    maintenanceBypass: "Maintenance Bypass",
    usersTab: "Users", settingsTab: "Settings", paymentsTab: "Payments", lastLogin: "Last Login", online: "Online", offline: "Offline", never: "Never",
    paymentsOverview: "Overview", subscriptionsTab: "Subscriptions", paymentHistoryTab: "Payments", paymentErrorsTab: "Errors", activityLogTab: "Activity Log",
    monthlyRecurring: "Monthly Recurring", pastDueWarning: "past due",
    stripeUnreachable: "Unreachable", stripeConnected: "Connected", stripeError: "Error",
    // Account deletion requests (admin)
    pendingDeletions: "Deletion requests",
    pendingDeletionsBanner: "open deletion requests",
    showDeletionRequests: "Show requests",
    showAllUsers: "Show all users",
    deleteAllRequested: "Delete all permanently",
    deletionRequestedBadge: "Deletion requested",
    bulkDeleteTitle: "Permanently delete multiple accounts",
    bulkDeleteDesc: "You are about to permanently delete all selected accounts and all associated data. Type the following word to confirm:",
    bulkDeleteConfirm: "Delete all permanently",
    bulkDeleteProgress: "Deleting...",
    gracePending: "Grace period active",
    graceReady: "Ready",
    deletionRequestedOn: "Requested on",
    deletionReason: "Reason",
    waitingFor: "in",
    daysShort: "d",
    of: "of",
    cancel: "Cancel",
    typeWord: "Type the security word...",
    deletionRequestedAlert: "Account deletion requested",
    deletionNoReason: "No reason provided",
    gracePeriodRemaining: "Grace period: {n} of 7 days remaining",
    gracePeriodEnded: "Grace period ended — ready for deletion",
    // Insights dashboard
    insightsTab: "Insights",
    totalUsers: "Total Users",
    paid: "Paid",
    free: "Free",
    paidShare: "Paid Share",
    freeShare: "Free Share",
    newUsersRange: "New Users",
    waitlistTotal: "Waitlist Total",
    waitlistPending: "Waitlist Pending",
    invitesSent: "Invites Sent",
    invitesConverted: "Converted",
    conversionRate: "Conversion Rate",
    invitesNotConverted: "Not Converted",
    staleInvites: "Stale Invites",
    customizeCards: "Customize Cards",
    hideCard: "Hide card",
    showCard: "Show card",
    onlineNow: "Online now",
    onlineNowDetail: "Active in the last {minutes} min",
    onlinePeak: "Peak online at once",
    onlinePeakDetail: "on {date}",
    onlinePeakNoRecord: "No peak recorded yet",
    onlineSinceTrackingStart: "since tracking start",
    onlineSinceReset: "since reset {date}",
    resetOnlinePeak: "Reset peak",
    resetOnlinePeakTitle: "Reset online peak?",
    resetOnlinePeakDescription: "The new peak will be set to the currently online users.",
    resetOnlinePeakConfirm: "Reset peak",
    refreshOnlinePresence: "Refresh online presence",
    onlinePeakResetSuccess: "Online peak reset",
    onlinePresenceLoadError: "Online presence could not be loaded",
    catUsers: "Users & Growth",
    catRevenue: "Subscriptions & Revenue",
    catWaitlist: "Waitlist & Funnel",
    catSystem: "System & Status",
    activeUsers: "Active Users",
    earlyAccessUsers: "Early Access Users",
    earlyAccessDetail: "Converted from waitlist (excl. test users)",
    activeSubscriptions: "Active Subs",
    mrr: "MRR",
    revenue: "Revenue",
    cancellations: "Cancellations",
    newCustomers: "New Customers",
    stripeStatus: "Stripe Status",
    stripeHealthy: "Connected",
    stripeUnhealthy: "Error",
    stripeLive: "Live",
    stripeTest: "Test",
    subsMonthly: "Monthly Subscriptions",
    subsYearly: "Yearly Subscriptions",
    subsLifetime: "Lifetime Members",
    subsMonthlyDetail: "Active monthly memberships",
    subsYearlyDetail: "Active yearly memberships",
    subsLifetimeDetail: "One-time purchase, permanent",
    // Sidebar navigation
    navUsers: "Users",
    navCommunity: "Community",
    navBusiness: "Business",
    navSystem: "System",
    navUserList: "Overview",
    navInsights: "Insights",
    navWaitlist: "Waitlist",
    navFeedback: "Feedback",
    navChangelog: "Changelog",
    navProject: "Project Board",
    navFaq: "FAQ",
    navPayments: "Payments",
    navProfitability: "Profitability",
    navProducts: "Products & Pricing",
    navSettings: "Settings",
    navSamples: "Samples",
  },
  upgrade: {
    title: "Premium", heroTitle: "Unlock Your Full Potential", heroSubtitle: "Take your drumming to the next level with MIDI, premium presets, and more.",
    monthly: "Monthly", yearly: "Yearly", lifetime: "Lifetime", perMonth: "month", perYear: "year", oneTime: "one-time",
    freeDesc: "Get started with the basics", premiumDesc: "Everything you need to level up",
    currentPlan: "Current Plan", recommended: "RECOMMENDED", subscribe: "Subscribe Now",
    yearlySaving: "Save 50% compared to monthly", lifetimeDesc: "Pay once, use forever",
    alreadyPremium: "You're on Premium!",
    manageSubscription: "Manage Subscription", successTitle: "Welcome to Premium!",
    comparisonTitle: "Feature Comparison", feature: "Feature",
    featurePatterns: "Drum Patterns & Basic Presets", featureMetronome: "Metronome & Practice Timer",
    featureStats: "Practice Statistics", featureMidi: "MIDI Controller Support",
    featurePremiumPresets: "Premium Presets", featureUnlimited: "Unlimited Saved Patterns",
    featureRecording: "Session Recording",
    featureMeasures: "Drum Pattern Measures",
    featureRecordingTime: "Recording Time",
    featurePracticeTracker: "Practice Tracker & Daily Routine",
    backToPlans: "Back to plans",
    checkoutTitle: "Complete your purchase",
    estimatedPriceNotice: "Estimated price. Final amount is determined at checkout based on current exchange rates and may vary.",
  },
  practiceTracker: {
    title: "Practice Tracker", subtitle: "Build your daily routine",
    today: "Today", songs: "Songs", rudiments: "Rudiments", warmups: "Warm-Ups", history: "History", library: "Library", session: "Session",
    addSong: "Add Song", addRudiment: "Add Rudiment", addWarmup: "Add Warm-Up", addItem: "Add",
    songTitle: "Song Title", songTitlePlaceholder: "e.g. Back in Black",
    artist: "Artist", artistPlaceholder: "e.g. AC/DC",
    difficulty: "Difficulty", selectDifficulty: "Select difficulty",
    beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced",
    personalNotes: "Notes", notesPlaceholder: "Your notes about this song...",
    rudimentName: "Rudiment", selectRudiment: "Select rudiment",
    exerciseName: "Exercise Name", exerciseNamePlaceholder: "e.g. Single Stroke Roll on Pad",
    exerciseDescription: "Description", descriptionPlaceholder: "Describe the exercise (e.g. stick control, pad work)...",
    durationMin: "Duration (min)", repetitions: "Repetitions",
    interval: "Practice Interval",
    intervals: { noSchedule: "No schedule (manual only)", daily: "Daily", every2: "Every 2 days", every3: "Every 3 days", every5: "Every 5 days", weekly: "Weekly" },
    save: "Save", cancel: "Cancel",
    itemAdded: "Item added", itemUpdated: "Item updated", itemDeleted: "Item deleted",
    noSongs: "No songs yet. Add your first song!", noRudiments: "No rudiments yet. Add your first rudiment!",
    noWarmups: "No warm-ups yet. Add your first exercise!",
    noItemsToday: "No items scheduled for today", addItemsHint: "Add items in the Songs, Rudiments, or Warm-Ups tabs",
    progress: "Today's Progress", finishSession: "Finish Session", sessionFinished: "Session saved!", startSession: "Start Session",
    launchMetronome: "Launch Metronome", openDrumPattern: "Open Drum Pattern",
    streakDays: "Day Streak", totalSessions: "Sessions", itemsCompleted: "Items Done",
    last90Days: "Last 90 Days", items: "items", recentSessions: "Recent Sessions",
    noSessions: "No sessions yet",
    premiumRequired: "Practice Tracker is a Premium feature",
    unlockPractice: "Unlock Practice Tracker",
    startTimer: "Start Timer", stopTimer: "Stop Timer", practicing: "Practicing...",
    totalPracticeTime: "Total Practice Time", practiceByCategory: "Time by Category",
    warmupTime: "Warm-Ups", rudimentTime: "Rudiments", songTime: "Songs",
    originalBpm: "Original BPM", practiceBpm: "Practice BPM", bpmProgress: "BPM Progress",
    skipToday: "Skip today", skippedToday: "Skipped for today", undoSkip: "Undo",
    noSessionRecorded: "No session recorded", noSessionPlanned: "No session planned", goToToday: "Today",
    startFreeSession: "Start Free Session", pickItemsForToday: "Pick items for today",
    addToToday: "Add for today", removeFromToday: "Remove", adhocItems: "Additional Items",
    selectItemsTitle: "Select Items", selectItemsDesc: "Choose items from your library for today's session",
    noItemsInLibrary: "No items in your library. Create songs, rudiments, or warm-ups first.",
    done: "Done",
    sessionSummaryTitle: "Session Complete!", sessionTotalDuration: "Total Duration",
    sessionItemsCompleted: "Items Completed", sessionGreatWork: "Great work! Keep it up.",
    sessionClose: "Close",
    sessionTitleLabel: "Session name", sessionTitlePlaceholder: "Name this session",
    sessionSaveTitle: "Save", sessionTitleSaved: "Session name saved",
    sessionAutoTitleFallback: "Free Session", sessionAutoTitleMoreOne: "1 more",
    sessionAutoTitleMoreMany: "{count} more",
    sessionHistoryAll: "Session History", sessionHistoryOpen: "View all",
    sessionHistoryLoadMore: "Load more",
    sessionHistoryToday: "Today", sessionHistoryYesterday: "Yesterday",
    linkedPatterns: "Linked Patterns", linkPattern: "Link Pattern",
    unlinkPattern: "Unlink", noPatterns: "No patterns available",
    createPatternFirst: "Create a drum pattern in the Pattern Editor first.",
    saveAsTemplate: "Save as Template", loadTemplate: "Load Template",
    templateName: "Template Name", templateNamePlaceholder: "e.g. Monday Routine",
    templateSaved: "Template saved", templateDeleted: "Template deleted",
    noTemplates: "No templates yet", templateLoaded: "Template loaded",
    deleteTemplate: "Delete Template",
    goalProgress: "Goal Progress",
    goalType: "Goal Type",
    goalNone: "No goal",
    goalTime: "Time goal",
    goalReps: "Repetitions goal",
    // YouTube & Video
    youtubeVideos: "YouTube Videos",
    youtubeUrlPlaceholder: "https://youtube.com/watch?v=...",
    videoLabelPlaceholder: "Label (optional)",
    addVideo: "Add",
    videoAdded: "Video added",
    videoRemoved: "Video removed",
    videoLoop: "Loop",
    videoSpeed: "Speed",
    // Inline Item Creator
    addNewItem: "Add New Item",
    addNewItemDesc: "Create a new item and add it to today's session",
    createAndAdd: "Create & Add",
    // Focus Overlay
    focusMode: "Focus Mode",
    originalBpmReached: "Original tempo reached!",
    nextUp: "Next",
    // Library
    allItems: "All", searchPlaceholder: "Search...",
    newCategory: "New Category", editCategory: "Edit Category",
    categoryNamePlaceholder: "e.g. Groove exercises", color: "Color", delete: "Delete",
  },
  dataReset: {
    title: "Reset Data",
    description: "Permanently delete your practice data. This cannot be undone.",
    resetStats: "Reset Statistics",
    resetStatsDesc: "Delete all practice completions and time tracking data.",
    resetSessions: "Reset Sessions",
    resetSessionsDesc: "Delete all saved practice sessions.",
    resetAll: "Reset Everything",
    resetAllDesc: "Delete all sessions, completions, and practice items.",
    confirmTitle: "Are you sure?",
    confirmDescription: "This action is irreversible. Type the word below to confirm:",
    typeWord: "Type here...",
    confirmWord: "Delete",
    cancel: "Cancel",
    confirm: "Confirm Delete",
    resetSuccess: "Data deleted successfully.",
    resetError: "Error deleting data.",
  },
  imprint: {
    title: "Legal Notice",
    legalInfoTitle: "Information pursuant to § 5 TMG",
    contactTitle: "Contact",
    disputeTitle: "Notice pursuant to § 36 VSBG",
    disputeText: "René Zimmermann is neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board.",
    euDisputeTitle: "EU Dispute Resolution",
    euDisputeText: "The European Commission provides an online dispute resolution (ODR) platform:",
    privacyLink: "Privacy Policy",
  },
  privacy: {
    title: "Privacy Policy",
    responsibleTitle: "1. Data Controller",
    responsibleText: "",
    principlesTitle: "2. Principles of Data Processing",
    principlesText: "We only process personal data to the extent permitted by law or with your consent. Processing is based on the GDPR, in particular Art. 6 GDPR.",
    hostingTitle: "3. Hosting & Infrastructure",
    hostingText: "This web app is provided via modern cloud infrastructure. When you access the website, your browser automatically transmits information to the server (server log files), including your IP address, date/time, requested URL, browser, and operating system. This data is required for the technical provision of the service (Art. 6(1)(f) GDPR) and is deleted after no more than 7 days.",
    accountTitle: "4. User Account & Data Storage (Supabase)",
    accountIntro: "Registration is available for using drumLog.pro. The following data is stored:",
    accountEmail: "Email address",
    accountPassword: "Password (encrypted/hashed, not stored in plain text)",
    accountPracticeData: "Practice data (sessions, patterns, progress)",
    accountSupabaseText: "This data is stored by Supabase Inc. The database server used is located in the EU (Frankfurt, AWS eu-central-1). Supabase is contractually bound as a data processor pursuant to Art. 28 GDPR. Legal basis: Art. 6(1)(b) GDPR (performance of contract).",
    accountSupabaseInfo: "Supabase Inc., 970 Toa Payoh North, Singapore",
    cookiesTitle: "6. Cookies & Local Storage",
    cookiesText: "This website uses cookies and localStorage. On your first visit, a cookie consent banner is displayed. Non-essential cookies (analytics, functional, marketing) are only set with your explicit consent pursuant to Art. 6(1)(a) GDPR in conjunction with § 25 TTDSG.",
    cookiesNecessary: "Necessary cookies (no consent required, § 25(2) TTDSG):",
    cookiesFunctional: "Functional cookies (only with consent, Art. 6(1)(a) GDPR in conjunction with § 25(1) TTDSG):",
    cookiesFunctionalWaitlist: "After signing up for the early-access waitlist, this stores a recognition marker (email hint) so the signup form is not shown to you again and you can see your confirmation status. Storage duration: 1 year. Automatically deleted when you withdraw your consent.",
    analyticsTitle: "7. Google Analytics",
    analyticsIntro: "With your consent, we use Google Analytics 4, a web analytics service provided by Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland.",
    analyticsCookies: "Google Analytics uses cookies that enable analysis of your use of the website. The information generated is usually transferred to a Google server in the USA. We have activated IP anonymization.",
    analyticsPrivacyFramework: "For data transfers to the USA, Google has committed to the EU-US Data Privacy Framework (EU Commission adequacy decision of July 10, 2023).",
    analyticsRevoke: "You can revoke your consent at any time via the cookie settings button (bottom left of the page).",
    analyticsLegal: "Legal basis: Art. 6(1)(a) GDPR in conjunction with § 25(1) TTDSG.",
    rightsTitle: "8. Your Rights (Art. 15–22 GDPR)",
    rightsText: "You have the right to access stored data (Art. 15 GDPR), rectification of inaccurate data (Art. 16 GDPR), erasure of your data (Art. 17 GDPR), restriction of processing (Art. 18 GDPR), data portability (Art. 20 GDPR), objection to processing (Art. 21 GDPR), and withdrawal of consent with effect for the future (Art. 7(3) GDPR).",
    rightsContact: "To exercise your rights, please contact:",
    rightsAuthority: "Competent supervisory authority:",
    securityTitle: "9. Data Security",
    securityText: "We employ technical and organizational security measures to protect your data against manipulation, loss, or unauthorized access. All data transmission is exclusively encrypted via HTTPS/TLS.",
    currentTitle: "10. Currency of this Privacy Policy",
    currentText: "This privacy policy is current as of: February 2025. Registered users will be informed of significant changes.",
    imprintLink: "Legal Notice",
  },
  cookieConsent: {
    title: "We value your privacy",
    description: "drumLog.pro uses cookies to give you the best possible experience. Essential cookies are required for the app to function. With your consent, we use additional cookies for analytics and functionality. You can withdraw your consent at any time.",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    managePreferences: "Manage Preferences",
    settingsDescription: "Choose which cookies you'd like to allow. Essential cookies cannot be disabled as they are required for core app functionality.",
    confirmSelection: "Confirm Selection",
    necessaryLabel: "Necessary – always active",
    necessaryDescription: "Session management, security, and core app functionality.",
    functionalLabel: "Functional",
    functionalDescription: "Saving practice settings, metronome configuration, and UI preferences.",
    analyticsLabel: "Analytics",
    analyticsDescription: "Anonymous usage statistics to improve the app.",
    marketingLabel: "Marketing",
    marketingDescription: "Third-party embeds and social sharing features.",
    privacyPolicy: "Privacy Policy",
    imprint: "Imprint",
    settingsAriaLabel: "Open cookie settings",
  },
  feedback: {
    submitTitle: "Submit Feedback", typeBug: "Bug Report", typeFeature: "Feature Request", typeFeedback: "Support",
    titleLabel: "Title", titlePlaceholder: "Brief summary...", descriptionLabel: "Description", descriptionPlaceholder: "Describe in detail...",
    stepsLabel: "Steps to Reproduce", stepsPlaceholder: "1. Go to...\n2. Click on...", expectedLabel: "Expected Behavior", expectedPlaceholder: "What should happen?",
    actualLabel: "Actual Behavior", actualPlaceholder: "What actually happens?", screenshots: "Screenshots", addScreenshot: "Add",
    submit: "Submit", submitted: "Feedback submitted!", supportSubmitted: "Support request sent successfully!",
    subscribeEmail: "Notify me by email", subscribedToast: "Subscribed — you'll receive email notifications",
    unsubscribedToast: "Unsubscribed", subscribeButton: "Subscribe", unsubscribeButton: "Subscribed",
    globalEmailEnabled: "Email notifications have been enabled globally",
    boardTitle: "Community Hub", featureRequests: "Feature Requests", bugTracker: "Bug Tracker", whatsNew: "What's New",
    roadmapTitle: "Roadmap", roadmap_planned: "Planned", roadmap_in_progress: "In Progress", roadmap_completed: "Completed", roadmapEmpty: "Nothing here yet.",
    noItems: "No items yet.", loginToVote: "Log in to vote", noChangelog: "No updates yet.",
    submitIdea: "Submit Idea", reportBug: "Report Bug",
    supportTab: "Support", submitSupport: "Send Request", supportTitle: "Contact Support",
    supportSubjectLabel: "Subject", supportSubjectPlaceholder: "What do you need help with?",
    supportMessageLabel: "Message", supportMessagePlaceholder: "Describe your issue or question...",
    contactEmailLabel: "Your Email", contactEmailPlaceholder: "your@email.com",
    contactEmailHint: "So we can reply to your request.", contactEmailRequired: "Please provide a contact email.",
    supportLoginHint: "Log in to view your support requests, or send a new one.",
    supportLogin: "Log in", noSupportItems: "No support requests yet.",
    loggedInAsLabel: "Logged in as", loggedInHint: "We'll reply to your account email.", supportLoginInstead: "Or log in instead",
  },
  subscription: {
    title: "Subscription",
    monthly: "Monthly", yearly: "Yearly", lifetime: "Lifetime",
    manage: "Manage Subscription", upgrade: "Upgrade to Premium",
    renewsOn: "Renews on", expiresOn: "Expires on",
    lifetimeAccess: "Lifetime access — no expiration",
    autoRenewalOn: "Auto-renewal on", autoRenewalOff: "Auto-renewal off",
    pastDue: "Payment past due — please update your payment method",
    paymentHistory: "Payment History",
  },
  waitlist: {
    title: "Secure your early access",
    subtitle: "Spots are limited. Sign up now to get in before everyone else — full access to drumLog.Pro before we go public.",
    badgeLimited: "Limited Early Access",
    badgeFree: "Free",
    benefitNoSpam: "No spam",
    benefitUnsubscribe: "No commitment",
    benefitFree: "Free",
    benefitPriority: "Priority before public launch",
    compactSubtitle: "Be among the first to try drumLog.Pro.",
    heroTitle: "Get in before everyone else",
    heroSubtitle: "Limited spots — secure yours now and skip the public launch queue.",
    emailPlaceholder: "Your email address",
    gdprConsent: "I agree that my email address is stored to reserve my early-access spot on the drumLog.Pro waitlist.",
    gdprLink: "Privacy Policy",
    submit: "Claim your spot",
    success: "You're on the list! We'll notify you when it's your turn.",
    alreadyRegistered: "This email is already on the waitlist.",
    accountExists: "An account with this email already exists.",
    disabled: "The waitlist is currently closed.",
    invalidEmail: "Please enter a valid email address.",
    completeProfileTitle: "Complete Your Profile",
    completeProfileSubtitle: "Set up your account to get started with drumLog.Pro.",
    setPassword: "Set a Password",
    profileCompleted: "Profile completed! Welcome to drumLog.Pro.",
    incompleteProfileBanner: "Your registration is not complete yet. Please finish setting up your account.",
    incompleteProfileCta: "Complete Profile",
    adminTab: "Waitlist",
    adminInvite: "Invite",
    adminInviteAll: "Invite All",
    adminDelete: "Delete",
    adminNoEntries: "No waitlist entries yet.",
    adminInvited: "Invited",
    adminInviteSuccess: "Invitation sent successfully.",
    adminInviteAllSuccess: "All invitations sent.",
    adminDeleteSuccess: "Entry deleted.",
    statusUnconfirmed: "Unconfirmed",
    statusPending: "Pending",
    statusInvited: "Invited",
    statusConverted: "Converted",
    statusRemoved: "Removed",
    waitlistEnabled: "Waitlist active",
    waitlistToggle: "Show waitlist signup on maintenance page",
    successCheckEmail: "Almost there! Confirm your email to lock in your early-access spot.",
    confirmPageTitle: "Email Confirmation",
    confirmPageSuccess: "Your email has been confirmed! You're now on the waitlist.",
    confirmPageError: "This confirmation link is invalid or has already been used.",
    confirmPageExpired: "This confirmation link has expired. Please sign up again.",
    confirmPageLoading: "Confirming your email...",
    rateLimited: "A confirmation email was already sent. Please check your inbox.",
    resendInviteTitle: "Invite Link Expired?",
    resendInviteSubtitle: "Enter your email address to receive a new invite link.",
    resendInviteButton: "Resend Invite Link",
    resendInviteSuccess: "If this email was invited, a new link has been sent. Please check your inbox.",
    alreadyOnListBadge: "You're on the list",
    alreadyOnListTitle: "Your early-access spot is reserved",
    alreadyOnListSubtitle: "We've got your email. As soon as a slot opens up, you'll be the first to know — no need to sign up again.",
    alreadyOnListHint: "Wrong email or signed up by mistake?",
    alreadyOnListReset: "Use a different email",
    pendingConfirmationBadge: "Confirmation pending",
    pendingConfirmationTitle: "Please confirm your email",
    pendingConfirmationSubtitle: "We've sent you a confirmation link. Click the link in the email to secure your spot on the early-access list.",
    pendingConfirmationCheckSpam: "Be sure to check your spam folder too.",
    pendingConfirmationResend: "Resend confirmation email",
    pendingConfirmationResending: "Sending...",
    pendingConfirmationResent: "Confirmation email resent. Please check your inbox.",
    pendingConfirmationResendError: "Sending failed. Please try again later.",
    resendInviteRateLimited: "A new invite was recently sent. Please wait a few minutes and check your inbox.",
    resendInviteLink: "Invite link expired?",
    adminFilterAll: "All",
    adminFilterStatus: "Filter by status",
    adminResendConfirmation: "Resend",
    adminReinvite: "Re-invite",
    adminBulkReinvite: "Re-invite All Stale",
    adminStaleInvites: "stale invite(s) — 14+ days without account creation",
    adminInviteClicked: "Link Clicked",
    adminCountry: "Country",
    adminSelected: "selected",
    adminResendConfirmationSuccess: "Confirmation email resent.",
    adminReinviteSuccess: "Re-invitation sent.",
    adminBulkReinviteSuccess: "Stale entries re-invited.",
    adminRemindIncomplete: "Remind",
    adminRemindIncompleteSuccess: "Reminder sent.",
  },
};

export default en;
